import { Component, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslocoService } from "@ngneat/transloco";
import { ToastrService } from "ngx-toastr";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { NgxFlickingComponent } from "@egjs/ngx-flicking";
import { StepperSelectionEvent } from "@angular/cdk/stepper";
import { faSquare } from "@fortawesome/free-regular-svg-icons";
import { Compbaser } from "../../../components/compbaser";
import { getFontIcons } from "../../../common/get-icons";
import { SceneMimeTypes } from "../scene-blocks/mime-constants";
import { isNullOrUndef, isNullOrUndefOrEmpty, SKIP_MIMES } from "../../../common";
import { AppService, CommonService, ContainerService, LocalStoreService, StoreSelectorsService } from "../../../services";
import { ICategoryCheckItemNode } from "../../../components/category-tree/tree-service/tree-data.model";
import { ISceneBlock, ISceneTypes, ISchedule, IUser } from "../../../interfaces";
import { DialogPopComponent } from "../../../components/dialog-pop/dialog-pop.component";
import { Lib } from "../../../../libs/Lib";
import * as _ from "lodash";

const contentWidth = 120;
const contentHeight = 135;

/**
 * @title Stepper with editable steps
 */
@Component({
    selector: "app-scene-create-wizard",
    templateUrl: "scene-create-wizard.component.html",
    styleUrls: ["scene-create-wizard.component.scss"],
})
export class SceneCreateWizardComponent extends Compbaser implements OnInit {
    @ViewChild(NgxFlickingComponent) flick: NgxFlickingComponent;

    readonly faSquare = faSquare;

    hDimensions = ['1920 x 1080', '3840 x 2160', '5120 x 2160', '7680 x 4320', '1366 x 768', '1440 x 900', '1280 x 800', '1280 x 1024', '1680 x 1050', '1680 x 900', '1920 x 1200', '1024 x 768', '1280 x 720', '2560 x 1440'];
    vDimensions = ['1080 x 1920', '2160 x 3840', '2160 x 5120', '4320 x 7680', '768 x 1366', '900 x 1440', '800 x 1280', '1024 x 1280', '1050 x 1680', '900 x 1680', '1200 x 1920', '768 x 1024', '720 x 1280', '1440 x 2560'];
    sceneContents = [
        [[5, 5, 110, 125, 1]],
        [[5, 5, 110, 70, 1], [80, 5, 110, 50]],
        [[5, 5, 110, 30], [40, 5, 110, 50], [95, 5, 110, 35, 1]],
        [[5, 5, 110, 30], [40, 5, 110, 30, 1], [75, 5, 50, 55], [75, 60, 55, 55]],
        [[5, 5, 110, 20, 1], [30, 5, 35, 50], [30, 45, 35, 50], [30, 85, 30, 50], [85, 5, 110, 45]],
        [[5, 5, 25, 35], [5, 35, 25, 35], [5, 65, 25, 35], [5, 95, 20, 35], [45, 5, 110, 20], [70, 5, 110, 35, 1], [110, 5, 110, 20]],
        [[5, 5, 50, 35], [5, 60, 55, 35], [45, 5, 110, 30], [80, 5, 50, 50], [80, 60, 55, 50, 1]],
        [[5, 5, 30, 125], [5, 40, 75, 125, 1]],
        [[5, 5, 35, 125], [5, 45, 35, 125, 1], [5, 85, 30, 125]],
        [[5, 5, 50, 35], [5, 60, 55, 35], [45, 5, 50, 40], [45, 60, 55, 40], [90, 5, 50, 40, 1], [90, 60, 55, 40]],
        [[5, 5, 25, 125], [5, 35, 25, 125, 1], [5, 65, 50, 60], [70, 65, 50, 60]],
        [[5, 5, 25, 25], [5, 35, 25, 25], [5, 65, 25, 25], [5, 95, 20, 25], [35, 5, 110, 95, 1]],
        [[5, 5, 25, 125], [5, 35, 80, 30, 1], [40, 35, 80, 50], [95, 35, 80, 35]],
        [[5, 5, 55, 125, 1], [5, 65, 50, 60], [70, 65, 50, 60]],
        // [[5, 5, 25, 25], [5, 35, 80, 25], [35, 5, 110, 95]],
        // [[5, 5, 35, 30], [5, 45, 35, 30], [5, 85, 30, 30], [40, 5, 35, 90], [40, 45, 35, 90], [40, 85, 30, 90]],
    ];

    /** component variables */
    enableAnimationEngine = true;
    disableCreate = true;
    propertyFormGroup: FormGroup;
    isEditable = true;
    categories: Array<string> = [];
    tabIndex = 0;
    stringJSPipeArgs = { humanize: [] };
    categoryTreeItems: Array<ICategoryCheckItemNode> = [];
    user: IUser;
    jsonTypes: ISceneTypes[] = [];
    schedules: ISchedule[] = [];
    selectedStep = 0;
    isFullFeatures;
    upgradable: boolean;

    /** new scene properties used for creating */
    keyPath = "";
    sceneType: "blank" | "category" | "component" = "blank";
    typeComponent = "";
    typeCategory: ISceneTypes;

    sceneStyle: "horizontal" | "vertical" | "custom" = "horizontal";
    dimensionIndex = "0";
    customSizeX = "";
    customSizeY = "";
    templateIndex = 0;
    sceneSchedule = "-1";
    maxSceneSize = 7680;
    presetAnimation;
    componentIcons = {
        scheduler: getFontIcons({ $: { id: "4103" } }).icon,
        playlist: getFontIcons({ $: { id: "4102" } }).icon,
        collection: getFontIcons({ $: { id: "4100" } }).icon,
    };

    constructor(
        public dialogRef: MatDialogRef<DialogPopComponent>,
        public override dialog: MatDialog,
        private _formBuilder: FormBuilder,
        private storeSelector: StoreSelectorsService,
        private toastr: ToastrService,
        private containerService: ContainerService,
        private router: Router,
        private appService: AppService,
        private commonService: CommonService,
        private translocoService: TranslocoService,
        private localStoreService: LocalStoreService,
    ) {
        super();
        this.categoryTreeItems = [];
        this.cod(
            this.storeSelector.isFullFeatures().subscribe((v) => {
                this.isFullFeatures = v;
            }),
        );

        this.jsonTypes = [];
        Object.keys(SceneMimeTypes).forEach((z) => {
            const v = SceneMimeTypes[z];
            if (this.storeSelector.isMainSceneOrPlayer(v)) {
                return;
            }
            if (SKIP_MIMES.indexOf(z) > -1) {
                return;
            }
            const block = this.storeSelector.getBlockLabelByMimeType(v, false);
            if (!block) return;
            // if (!this.isFullFeatures) {
            //     if (LIMITED_MIME_RELEASE.findIndex((m) => m === v) === -1)
            //         return;
            // }

            this.jsonTypes.push({ label: z, icon: block.icon ? block.icon : "", mimeType: v, enterprise: block.enterprise });
        });
        this.applyMimeOrder();

        this.propertyFormGroup = this._formBuilder.group({
            sceneName: ["", Validators.required],
            playerAssignable: [false],
            scheduleName: [""],
            scheduleDesc: [""],
        });
    }

    ngOnInit() {
        this.cod(
            this.storeSelector.getUser().subscribe((i_user: IUser) => {
                this.user = i_user;
                this.upgradable = Lib.IsAccountUpgradable(i_user);
            }),
        );
        this.cod(
            this.storeSelector.getSchedules().subscribe((i_items: ISchedule[]) => {
                this.schedules = i_items;
            }),
        );
        this.cod(
            this.containerService.getCategories().subscribe((i_categories) => {
                this.categories = i_categories ? i_categories : [];
            }),
        );
        this.dimensionIndex = this.localStoreService.getStorageValConfig("dimensionIndex") || 0;
        this.customSizeX = this.localStoreService.getStorageValConfig("customSizeX");
        this.customSizeY = this.localStoreService.getStorageValConfig("customSizeY");
    }

    async go(val) {
        if (isNullOrUndef(val)) return;

        try {
            await this.flick.moveTo(val);
            this.tabIndex = val;
            switch (val) {
                case "0": {
                    this.sceneStyle = "horizontal";
                    break;
                }
                case "1": {
                    this.sceneStyle = "vertical";
                    break;
                }
                case "2": {
                    this.sceneStyle = "custom";
                    break;
                }
            }
        } catch (err) {
            console.log(err);
        }
    }

    selectSceneType(sceneType, value, needUpgrade = null) {
        if (needUpgrade) {
            this.upgrade();
            return;
        }

        this.sceneType = sceneType;
        this.typeComponent = null;
        this.typeCategory = null;

        if (sceneType === "component") {
            this.typeComponent = value;
        } else if (sceneType === "category") {
            this.typeCategory = value;
        }
        this.enableAnimationEngine = sceneType !== "category";
        this.checkCreateButtonStatus();
    }

    changeSceneCategory(e: any): void {
        this.keyPath = e;
    }

    setTemplate(val) {
        if (!val) val = 0;
        this.templateIndex = val;
    }

    onChangeScheduleName() {
        this.checkCreateButtonStatus();
    }

    onChangeSceneName() {
        this.checkCreateButtonStatus();
    }

    createAnimationSlice() {
        this.enableAnimationEngine = true;
    }

    removeAnimationSlice(event) {
        this.enableAnimationEngine = false;
    }

    private checkCreateButtonStatus() {
        const sceneName = this.propertyFormGroup.get("sceneName").value?.trim();
        if (!sceneName) {
            this.disableCreate = true;
            return;
        }

        const scheduleName = this.propertyFormGroup.get("scheduleName").value?.trim();
        if (this.sceneType === "component" && this.typeComponent === "scheduler") {
            this.disableCreate = !scheduleName;
            return;
        }
        this.disableCreate = false;
    }

    applyMimeOrder() {
        this.jsonTypes = this.jsonTypes.sort((j) => {
            if (j.enterprise) return 1;
            return -1;
        });
    }

    upgrade() {
        this.appService.openWizardUpgradeDialog();
    }

    onStepChange(e: StepperSelectionEvent) {
        this.selectedStep = e.selectedIndex;
    }

    presetSceneAnimation(e) {
        this.presetAnimation = e;
    }

    async createScene() {
        const sceneName = this.propertyFormGroup.get("sceneName").value;
        // tslint:disable-next-line
        const w = parseInt(this.getDimension()[0]);
        // tslint:disable-next-line
        const h = parseInt(this.getDimension()[1]);
        if (!w || !h) {
            this.toastr.info(this.translocoService.translate("general.sceneDimensions", {}, this.translocoService.getActiveLang()));
            return;
        }
        if (w > this.maxSceneSize || h > this.maxSceneSize) {
            this.toastr.info(this.translocoService.translate("general.maxSceneSize", {}, this.translocoService.getActiveLang()) + " " + this.maxSceneSize + "px");
            return;
        }
        this.localStoreService.setStorageValConfigGroup([
            {key: 'dimensionIndex', val: this.dimensionIndex},
            {key: 'customSizeX', val: this.customSizeX},
            {key: 'customSizeY', val: this.customSizeY}
        ]);

        // get scene template
        const sceneId = _.uniqueId();
        const sceneBlockLabel = this.storeSelector.getBlockLabelByFileType("scene");
        const scene: ISceneBlock = sceneBlockLabel.template;
        const newScene = { Player: scene };

        // set scene layout
        newScene.Player.$.id = sceneId;
        newScene.Player.$.player = sceneBlockLabel.id;
        newScene.Player.Data[0].Layout[0].$.width = w + "";
        newScene.Player.Data[0].Layout[0].$.height = h + "";
        newScene.Player.$.label = sceneName;
        newScene.Player.$.folder = this.keyPath;
        newScene.Player.$.mimeType = SceneMimeTypes.Player;
        if (this.presetAnimation && this.enableAnimationEngine) {
            newScene.Player.Data[0].Animation = [this.presetAnimation];
        } else {
            newScene.Player.Data[0].Animation = [];
        }

        const playerAssignable = this.propertyFormGroup.get("playerAssignable").value;
        if (this.sceneType != "category") {
            // set mimetype
            newScene.Player.$.mimeType = playerAssignable ? SceneMimeTypes.Player : SceneMimeTypes.Scene;
            newScene.Player.$.scheduleId = this.sceneSchedule;
        }

        // set guidelines or add components
        if (this.sceneType == "blank") {
            // const pattern = this.sceneContents.blank[this.templateIndex].type;
            // get guidelines from selected pattern image
            // const guides = getGuidelineFromPattern(pattern, w, h);
            // newScene.Player.$.guides = {
            //     horizontal: guides.guidelines.horizontal.map((v) => this.roundLinePercent(v / h)).join(),
            //     vertical: guides.guidelines.vertical.map((v) => this.roundLinePercent(v / w)).join(),
            //     opacity: '1',
            //     hidden: guides.hidelines
            // };
        } else {
            const subContents: Array<number[]> = this.sceneContents[this.templateIndex];
            if (subContents) {
                const blockDataList: Array<ISceneBlock> = [];
                for (const content of subContents) {
                    const blockData = await this.getBlockData(content, w, h);
                    if (blockData) {
                        blockDataList.push(blockData);
                    }
                }

                // add patterns to scene
                newScene.Player.Data[0].Scene[0].Players[0].Player = blockDataList;
            }

            // set mimetype for category scene
            if (this.sceneType == "category" && this.typeCategory) {
                if (!isNullOrUndefOrEmpty(this.typeCategory.mimeType)) {
                    newScene.Player.$.mimeType = this.typeCategory.mimeType;
                }
            }
        }

        this.dialogRef.close(newScene);
    }

    // moveEndStyle(e) {
    //     this.flick.index === 1 ? 'vertical' : 'horizontal';
    // }

    private getDimension() {
        switch (this.sceneStyle) {
            case "custom": {
                return [this.customSizeX, this.customSizeY];
            }
            case "vertical": {
                const d = this.vDimensions[this.dimensionIndex];
                return d.split(" x ");
            }
            case "horizontal": {
                const d = this.hDimensions[this.dimensionIndex];
                return d.split(" x ");
            }
        }
    }

    /** round number to 3 decimals */
    private roundLinePercent(decimal) {
        return Math.round(decimal * 10000) / 10000;
    }

    /**
     * layout: component size [top, left, width, height]: ex. [0, 0, 100, 100, 1] (1: special component)
     * width: scene width
     * height: scene height
     */
    private async getBlockData(layout: number[], width, height) {
        if (!layout) return null;
        const isSpecialComponent = layout[4];
        if (!isSpecialComponent) return null;

        /** horizontal template is same as transform: rotate(-90deg) */
        const ww = this.sceneStyle == "vertical" ? width : height;
        const hh = this.sceneStyle == "vertical" ? height : width;

        const x = Math.floor((layout[1] * ww) / contentWidth);
        const y = Math.floor((layout[0] * hh) / contentHeight);
        const w = Math.floor((layout[2] * ww) / contentWidth);
        const h = Math.floor((layout[3] * hh) / contentHeight);

        let blockId = "3241"; // BlockLabelComponent
        let blockData: ISceneBlock;

        if (this.sceneType == "component") {
            if (this.typeComponent == "scheduler") {
                blockId = "4104"; // BlockSchedulerComponent
            } else if (this.typeComponent == "playlist") {
                blockId = "4102"; // BlockPlaylistComponent
            } else if (this.typeComponent == "collection") {
                blockId = "4100"; // BlockCollectionComponent
            }

            /** get block template by BlockLabel id */
            blockData = this.storeSelector.getBlockLabelById(blockId).template;
            if (blockId == "4104") {
                blockData.Data[0].Schedule[0].$.id = await this.commonService.addSchedule({
                    schedule_name: this.propertyFormGroup.get("scheduleName").value,
                    description: this.propertyFormGroup.get("scheduleDesc").value,
                });
            }
        } else if (this.sceneType == "category") {
            /** get block template by mimeType */
            if (!isNullOrUndefOrEmpty(this.typeCategory.mimeType)) {
                blockData = this.storeSelector.getBlockLabelByMimeType(this.typeCategory.mimeType, true).template;
            } else {
                return null;
            }
        }

        /** set layout of each block */
        if (blockData) {
            blockData.Data[0].Layout[0].$.x = this.sceneStyle == "vertical" ? x + "" : y + "";
            blockData.Data[0].Layout[0].$.y = this.sceneStyle == "vertical" ? y + "" : height - w - x + "";
            blockData.Data[0].Layout[0].$.height = this.sceneStyle == "vertical" ? h + "" : w + "";
            blockData.Data[0].Layout[0].$.width = this.sceneStyle == "vertical" ? w + "" : h + "";
            blockData.$.id = _.uniqueId("__");
            return blockData;
        }
        return null;
    }

    onClose() {
        this.dialogRef.close();
    }
}
